import { useEffect } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { useStore } from '@/utils/storeUtils';
import getContext from '@/utils/getContext';
import { buildInitialLegacyState } from '@/utils/routeState/builders/buildInitialLegacyState';
import { GlobalStoreWithRoutes, RouteStateMap } from '../../types/routeState';

export function useRouteHydration<K extends keyof RouteStateMap>(routeKey: K) {
  const [, updateStore] = useStore<GlobalStoreWithRoutes>();

  useEffect(() => {
    updateStore((current: GlobalStoreWithRoutes) => {
      // Early return if already hydrated
      if (current.hydrated?.[routeKey]) return current;

      // Get API data from store or context
      let apiData = current.apiData?.[routeKey];

      if (!apiData) {
        try {
          apiData = getContext();
        } catch (error) {
          console.error(`[Hydration] Failed to get context for '${routeKey}':`, error);
          datadogRum.addError(error, {
            module: 'RouteHydration',
            route_key: routeKey,
            failure_reason: 'getContext_exception',
          });
          return current;
        }
      }

      if (!apiData) {
        console.warn(`[Hydration] No apiData found for route '${routeKey}'`);
        datadogRum.addError(
          new Error(`Hydration failed: No apiData found for route '${routeKey}'`),
          {
            module: 'RouteHydration',
            route_key: routeKey,
            failure_reason: 'no_api_data',
            context_element_exists: !!document.getElementById('context_data'),
          },
        );
        return current;
      }

      // Update apiData if it came from context
      const nextApiData = { ...(current.apiData || {}), [routeKey]: apiData };

      if (routeKey === 'cart') {
        return {
          ...current,
          apiData: nextApiData,
          ...apiData,
          routes: { ...(current.routes || {}), cart: apiData },
          hydrated: { ...(current.hydrated || {}), cart: true },
        };
      }
      const initial = buildInitialLegacyState(apiData);

      return {
        ...current,
        apiData: nextApiData,
        ...initial,
        hydrated: { ...(current.hydrated || {}), [routeKey]: true },
      };
    });
  }, [routeKey]);
}
